import { ApiService } from './apiService';
import { API_ENDPOINTS } from '../config/api';
import { appendQueryString } from '../utils/queryParams';
import { formatDueDateForApi } from '../utils/taskDate';
import {
  BoardFilters,
  CreateKanbanTaskRequest,
  CreateTaskLabelRequest,
  CreateTaskRequest,
  KanbanBoardColumn,
  KanbanTask,
  TaskCard,
  TaskComment,
  TaskHistoryResponse,
  TaskLabelRef,
  TaskPriority,
  TaskStatus,
  TaskTypeRef,
  TaskUserRef,
  UpdateKanbanTaskRequest,
  UpdateTaskLabelRequest,
  UpdateTaskRequest,
  KanbanComment,
} from '../types/kanban';

export const KANBAN_COLUMNS = [
  { id: 'un_assigned' as const, title: 'Unassigned', dotColor: '#3b82f6' },
  { id: 'todo' as const, title: 'To Do', dotColor: '#3b82f6' },
  { id: 'waiting_for_info' as const, title: 'Waiting for Info', dotColor: '#eab308' },
  { id: 'in_progress' as const, title: 'In Progress', dotColor: '#3b82f6' },
  { id: 'completed' as const, title: 'Completed', dotColor: '#22c55e' },
];

const DEFAULT_COLUMN_ORDER: TaskStatus[] = [
  'un_assigned',
  'todo',
  'waiting_for_info',
  'in_progress',
  'completed',
];

export const mapTaskCardToKanban = (task: TaskCard, order = 0): KanbanTask => ({
  taskId: String(task.taskId),
  taskNumber: task.taskNumber,
  columnId: task.status,
  title: task.title,
  email: task.email,
  description: task.description ?? undefined,
  priority: task.priority,
  labels: task.labels ?? [],
  typeName: task.type?.name ?? null,
  dueDate: task.dueDate,
  assignee: task.assignee
    ? {
        id: String(task.assignee.userId),
        name: task.assignee.name,
        avatar: task.assignee.avatarUrl ?? undefined,
      }
    : undefined,
  reporter: task.reporter
    ? { id: String(task.reporter.userId), name: task.reporter.name }
    : undefined,
  leadId: task.leadId != null ? String(task.leadId) : undefined,
  leadUniqueId:
    task.leadUniqueId
    ?? (task as TaskCard & { lead_unique_id?: string | null }).lead_unique_id
    ?? undefined,
  leadUserId:
    task.userId != null
      ? String(task.userId)
      : (task as TaskCard & { user_id?: number | string | null }).user_id != null
        ? String((task as TaskCard & { user_id?: number | string | null }).user_id)
        : undefined,
  isClosed: task.isClosed,
  order,
});

const buildMockBoard = (): KanbanBoardColumn[] => {
  const mockTasks: TaskCard[] = [
    {
      taskId: 1,
      taskNumber: 'TKT-00001',
      title: 'Global Tech Hub',
      email: 'contact@globaltechhub.com',
      status: 'un_assigned',
      priority: 'high',
      dueDate: '12 10 2026',
      type: { id: 1, name: 'Facebook' },
      labels: [{ labelId: 2, name: 'Warm', colorHex: '#FFB300' }],
      assignee: null,
      reporter: null,
      leadId: 5,
      leadUniqueId: 'LD-00005',
      completedAt: null,
      isClosed: false,
      createdAt: new Date().toISOString(),
      modifiedAt: new Date().toISOString(),
    },
    {
      taskId: 2,
      taskNumber: 'TKT-00002',
      title: 'Design New Landing Page',
      email: 'contact@globaltechhub.com',
      description:
        'Develop a modern, mobile-responsive landing page for the new CRM campaign.\n\nRequirements:\n• Hero section with clear CTA\n• Feature highlights section\n• Mobile-first responsive layout\n• Integration with existing brand guidelines',
      status: 'in_progress',
      priority: 'high',
      dueDate: '15 10 2023',
      type: { id: 1, name: 'Facebook' },
      labels: [
        { labelId: 4, name: 'Design', colorHex: '#757575' },
        { labelId: 5, name: 'High Priority', colorHex: '#43A047' },
      ],
      assignee: {
        userId: 12,
        name: 'Alex Rivera',
        email: 'alex@example.com',
        avatarUrl: null,
      },
      reporter: { userId: 8, name: 'Jessica White', email: 'jessica@example.com' },
      leadId: 44092,
      leadUniqueId: 'LD-44092',
      completedAt: null,
      isClosed: false,
      createdAt: new Date().toISOString(),
      modifiedAt: new Date().toISOString(),
    },
    {
      taskId: 3,
      taskNumber: 'TKT-00003',
      title: 'Inovate Tech',
      email: 'billing@inovate.tech',
      status: 'completed',
      priority: 'low',
      dueDate: '08 10 2025',
      type: { id: 4, name: 'Outbound' },
      labels: [{ labelId: 3, name: 'Cold', colorHex: '#0284C7' }],
      assignee: {
        userId: 7,
        name: 'Rohan Joshi',
        avatarUrl: null,
      },
      reporter: { userId: 9, name: 'Rachel Kim', email: 'rachel@example.com' },
      leadId: null,
      completedAt: new Date().toISOString(),
      isClosed: true,
      createdAt: new Date().toISOString(),
      modifiedAt: new Date().toISOString(),
    },
  ];

  return DEFAULT_COLUMN_ORDER.map((status) => {
    const tasks = mockTasks.filter((t) => t.status === status);
    return { status, count: tasks.length, tasks };
  });
};

let mockBoard = buildMockBoard();

const MOCK_LABEL_PALETTE = [
  '#E53935', '#D81B60', '#8E24AA', '#5E35B1', '#3949AB',
  '#1E88E5', '#039BE5', '#00ACC1', '#00897B', '#43A047',
  '#7CB342', '#C0CA33', '#FDD835', '#FFB300', '#FB8C00',
  '#F4511E', '#6D4C41', '#757575', '#546E7A', '#263238',
];

let mockTaskLabels: TaskLabelRef[] = [
  { labelId: 1, name: 'Hot', colorHex: '#E53935' },
  { labelId: 2, name: 'Warm', colorHex: '#FFB300' },
  { labelId: 3, name: 'Cold', colorHex: '#1E88E5' },
  { labelId: 4, name: 'Design', colorHex: '#757575' },
  { labelId: 5, name: 'High Priority', colorHex: '#43A047' },
];

let nextMockLabelId = 6;

const filterMockBoard = (filters?: BoardFilters): KanbanBoardColumn[] => {
  let columns = mockBoard.map((col) => ({
    ...col,
    tasks: [...col.tasks],
  }));

  if (filters?.status?.length) {
    columns = columns.filter((c) => filters.status!.includes(c.status));
  }

  const applyTaskFilters = (tasks: TaskCard[]) => {
    let result = tasks;
    if (filters?.search?.trim()) {
      const q = filters.search.trim().toLowerCase();
      result = result.filter(
        (t) =>
          t.title.toLowerCase().includes(q) ||
          t.email.toLowerCase().includes(q) ||
          t.taskNumber.toLowerCase().includes(q)
      );
    }
    if (filters?.assigneeUserIds?.length) {
      result = result.filter(
        (t) =>
          t.assignee &&
          filters.assigneeUserIds!.includes(Number(t.assignee.userId))
      );
    }
    if (filters?.priorities?.length) {
      result = result.filter(
        (t) => t.priority && filters.priorities!.includes(t.priority)
      );
    }
    if (filters?.labelIds?.length) {
      result = result.filter((t) =>
        t.labels.some((l) => filters.labelIds!.includes(l.labelId))
      );
    }
    return result;
  };

  return columns.map((col) => {
    const tasks = applyTaskFilters(col.tasks);
    return { ...col, tasks, count: tasks.length };
  });
};

const toCreatePayload = (data: CreateKanbanTaskRequest): CreateTaskRequest => ({
  title: data.title,
  email: data.email,
  description: data.description,
  leadId: data.leadId ?? null,
  taskTypeId: data.taskTypeId,
  status: data.columnId,
  priority: data.priority ?? null,
  dueDate: data.dueDate ? formatDueDateForApi(data.dueDate) : undefined,
  assigneeUserId: data.assigneeId ? Number(data.assigneeId) : undefined,
  reporterUserId: data.reporterId ? Number(data.reporterId) : undefined,
  labelIds: data.labelIds,
});

const toUpdatePayload = (data: UpdateKanbanTaskRequest): UpdateTaskRequest => {
  const payload: UpdateTaskRequest = {};
  if (data.title !== undefined) payload.title = data.title;
  if (data.email !== undefined) payload.email = data.email;
  if (data.description !== undefined) payload.description = data.description;
  if (data.leadId !== undefined) payload.leadId = data.leadId;
  if (data.columnId !== undefined) payload.status = data.columnId;
  if (data.priority !== undefined) payload.priority = data.priority ?? undefined;
  if (data.taskTypeId !== undefined) payload.taskTypeId = data.taskTypeId ?? undefined;
  if (data.dueDate !== undefined) {
    payload.dueDate = data.dueDate ? formatDueDateForApi(data.dueDate) : undefined;
  }
  if (data.assigneeId !== undefined) {
    payload.assigneeUserId = data.assigneeId ? Number(data.assigneeId) : undefined;
  }
  if (data.reporterId !== undefined) {
    payload.reporterUserId = data.reporterId ? Number(data.reporterId) : undefined;
  }
  if (data.labelIds !== undefined) payload.labelIds = data.labelIds;
  if (data.clearAssignee) payload.clearAssignee = true;
  if (data.clearReporter) payload.clearReporter = true;
  if (data.clearPriority) payload.clearPriority = true;
  if (data.clearDueDate) payload.clearDueDate = true;
  if (data.clearTaskType) payload.clearTaskType = true;
  if (data.clearLead) payload.clearLead = true;
  return payload;
};

export class KanbanService extends ApiService {
  async fetchBoard(filters?: BoardFilters): Promise<KanbanBoardColumn[]> {
    try {
      const response = await this.post<{ columns: KanbanBoardColumn[] }>(
        API_ENDPOINTS.kanban.board,
        filters ?? {}
      );
      return response.columns ?? [];
    } catch {
      return filterMockBoard(filters);
    }
  }

  async getTaskById(id: string): Promise<TaskCard | null> {
    try {
      return await this.get<TaskCard>(API_ENDPOINTS.kanban.get(id));
    } catch {
      for (const col of mockBoard) {
        const found = col.tasks.find((t) => String(t.taskId) === id);
        if (found) return found;
      }
      return null;
    }
  }

  async createTask(data: CreateKanbanTaskRequest): Promise<TaskCard> {
    try {
      return await this.post<TaskCard>(API_ENDPOINTS.kanban.create, toCreatePayload(data));
    } catch {
      const id =
        mockBoard.reduce(
          (max, c) => Math.max(max, ...c.tasks.map((t) => Number(t.taskId)), 0),
          0
        ) + 1;
      const task: TaskCard = {
        taskId: id,
        taskNumber: `TKT-${String(id).padStart(5, '0')}`,
        title: data.title,
        email: data.email,
        description: data.description ?? null,
        status: data.columnId,
        priority: data.priority ?? null,
        dueDate: data.dueDate ? formatDueDateForApi(data.dueDate) ?? null : null,
        type: data.taskTypeId ? { id: data.taskTypeId, name: 'Organic' } : null,
        labels: (data.labelIds ?? [])
          .map((id) => mockTaskLabels.find((l) => l.labelId === id))
          .filter((l): l is TaskLabelRef => Boolean(l)),
        assignee: null,
        reporter: null,
        leadId: data.leadId ?? null,
        completedAt: null,
        isClosed: data.columnId === 'completed',
        createdAt: new Date().toISOString(),
        modifiedAt: new Date().toISOString(),
      };
      mockBoard = mockBoard.map((col) =>
        col.status === task.status
          ? { ...col, tasks: [...col.tasks, task], count: col.count + 1 }
          : col
      );
      return task;
    }
  }

  async updateTask(id: string, data: UpdateKanbanTaskRequest): Promise<TaskCard> {
    try {
      return await this.patch<TaskCard>(API_ENDPOINTS.kanban.update(id), toUpdatePayload(data));
    } catch {
      let updated: TaskCard | null = null;
      mockBoard = mockBoard.map((col) => ({
        ...col,
        tasks: col.tasks.map((t) => {
          if (String(t.taskId) !== id) return t;
          const labels =
            data.labelIds !== undefined
              ? data.labelIds
                  .map((id) => mockTaskLabels.find((l) => l.labelId === id))
                  .filter((l): l is TaskLabelRef => Boolean(l))
              : t.labels;
          updated = {
            ...t,
            title: data.title ?? t.title,
            email: data.email ?? t.email,
            description: data.description ?? t.description,
            leadId: data.leadId ?? t.leadId,
            status: data.columnId ?? t.status,
            priority: data.clearPriority ? null : data.priority ?? t.priority,
            dueDate: data.clearDueDate ? null : t.dueDate,
            labels,
            isClosed: (data.columnId ?? t.status) === 'completed',
          };
          return updated;
        }),
      }));
      if (!updated) throw new Error('Task not found');
      return updated;
    }
  }

  async updateTaskStatus(id: string, status: TaskStatus): Promise<TaskCard> {
    try {
      return await this.patch<TaskCard>(API_ENDPOINTS.kanban.updateStatus(id), { status });
    } catch {
      return this.updateTask(id, { columnId: status });
    }
  }

  async deleteTask(id: string): Promise<void> {
    try {
      await this.delete<void>(API_ENDPOINTS.kanban.delete(id));
    } catch {
      mockBoard = mockBoard.map((col) => {
        const tasks = col.tasks.filter((t) => String(t.taskId) !== id);
        return { ...col, tasks, count: tasks.length };
      });
    }
  }

  async getAssignees(): Promise<TaskUserRef[]> {
    try {
      return await this.get<TaskUserRef[]>(API_ENDPOINTS.kanban.assignees);
    } catch {
      return [
        { userId: 12, name: 'Alex Rivera', email: 'alex@example.com', role: 'manager' },
        { userId: 8, name: 'Jessica White', email: 'jessica@example.com', role: 'admin' },
        { userId: 2, name: 'Priya Patel', role: 'relationship_manager' },
        { userId: 7, name: 'Rohan Joshi', role: 'sales_executive' },
      ];
    }
  }

  async getReporters(): Promise<TaskUserRef[]> {
    try {
      return await this.get<TaskUserRef[]>(API_ENDPOINTS.kanban.reporters);
    } catch {
      return [
        { userId: 8, name: 'Jessica White', role: 'admin' },
        { userId: 12, name: 'Alex Rivera', role: 'manager' },
      ];
    }
  }

  async getTaskTypes(): Promise<TaskTypeRef[]> {
    try {
      return await this.get<TaskTypeRef[]>(API_ENDPOINTS.kanban.taskTypes);
    } catch {
      return [
        { id: 1, name: 'Facebook' },
        { id: 2, name: 'Instagram' },
        { id: 3, name: 'Organic' },
      ];
    }
  }

  async getLabelPalette(): Promise<string[]> {
    try {
      return await this.get<string[]>(API_ENDPOINTS.kanban.labelPalette);
    } catch {
      return [...MOCK_LABEL_PALETTE];
    }
  }

  async getTaskLabels(): Promise<TaskLabelRef[]> {
    try {
      return await this.get<TaskLabelRef[]>(API_ENDPOINTS.kanban.labels);
    } catch {
      return [...mockTaskLabels];
    }
  }

  async createTaskLabel(body: CreateTaskLabelRequest): Promise<TaskLabelRef> {
    try {
      return await this.post<TaskLabelRef>(API_ENDPOINTS.kanban.labels, body);
    } catch {
      const existing = mockTaskLabels.find(
        (l) => l.name.toLowerCase() === body.name.trim().toLowerCase()
      );
      if (existing) return existing;
      const created: TaskLabelRef = {
        labelId: nextMockLabelId++,
        name: body.name.trim(),
        colorHex: body.colorHex.toUpperCase(),
      };
      mockTaskLabels = [...mockTaskLabels, created].sort((a, b) =>
        a.name.localeCompare(b.name)
      );
      return created;
    }
  }

  async updateTaskLabel(id: string, body: UpdateTaskLabelRequest): Promise<TaskLabelRef> {
    try {
      return await this.patch<TaskLabelRef>(API_ENDPOINTS.kanban.label(id), body);
    } catch {
      const labelId = Number(id);
      const index = mockTaskLabels.findIndex((l) => l.labelId === labelId);
      if (index === -1) throw new Error('Label not found');
      mockTaskLabels[index] = {
        ...mockTaskLabels[index],
        ...(body.name !== undefined ? { name: body.name.trim() } : {}),
        ...(body.colorHex !== undefined ? { colorHex: body.colorHex.toUpperCase() } : {}),
      };
      return mockTaskLabels[index];
    }
  }

  async deleteTaskLabel(id: string): Promise<void> {
    try {
      await this.delete<void>(API_ENDPOINTS.kanban.label(id));
    } catch {
      const labelId = Number(id);
      mockTaskLabels = mockTaskLabels.filter((l) => l.labelId !== labelId);
      mockBoard = mockBoard.map((col) => ({
        ...col,
        tasks: col.tasks.map((t) => ({
          ...t,
          labels: t.labels.filter((l) => l.labelId !== labelId),
        })),
      }));
    }
  }

  async getComments(taskId: string): Promise<{ count: number; comments: TaskComment[] }> {
    try {
      return await this.get<{ count: number; comments: TaskComment[] }>(
        API_ENDPOINTS.kanban.comments(taskId)
      );
    } catch {
      if (taskId === '2') {
        return {
          count: 2,
          comments: [
            {
              commentId: 1,
              body: 'Please share the latest wireframes before EOD.',
              author: { userId: 12, name: 'Alex Rivera' },
              attachments: [],
              createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
              modifiedAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
            },
            {
              commentId: 2,
              body: 'Updated the hero section copy based on client feedback.',
              author: { userId: 8, name: 'Jessica White' },
              attachments: [],
              createdAt: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
              modifiedAt: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
            },
          ],
        };
      }
      return { count: 0, comments: [] };
    }
  }

  async createComment(
    taskId: string,
    body: string,
    fileIds?: number[]
  ): Promise<TaskComment> {
    return this.post<TaskComment>(API_ENDPOINTS.kanban.comments(taskId), {
      body,
      fileIds: fileIds?.length ? fileIds : undefined,
    });
  }

  async updateComment(
    taskId: string,
    commentId: string,
    body: string
  ): Promise<TaskComment> {
    return this.patch<TaskComment>(
      API_ENDPOINTS.kanban.comment(taskId, commentId),
      { body }
    );
  }

  async deleteComment(taskId: string, commentId: string): Promise<void> {
    await this.delete<void>(API_ENDPOINTS.kanban.comment(taskId, commentId));
  }

  async getHistory(
    taskId: string,
    page = 1,
    limit = 50
  ): Promise<TaskHistoryResponse> {
    try {
      const url = appendQueryString(API_ENDPOINTS.kanban.history(taskId), { page, limit });
      return await this.get<TaskHistoryResponse>(url);
    } catch {
      return {
        total: 2,
        page: 1,
        limit: 50,
        items: [
          {
            historyId: 1,
            entity: 'task',
            fieldName: 'status',
            oldValue: 'todo',
            newValue: 'in_progress',
            action: 'update',
            changedBy: { userId: 8, name: 'Jessica White' },
            createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
          },
          {
            historyId: 2,
            entity: 'task',
            fieldName: 'priority',
            oldValue: 'normal',
            newValue: 'high',
            action: 'update',
            changedBy: { userId: 12, name: 'Alex Rivera' },
            createdAt: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
          },
        ],
      };
    }
  }

  boardToKanbanTasks(columns: KanbanBoardColumn[]): KanbanTask[] {
    const tasks: KanbanTask[] = [];
    columns.forEach((col) => {
      col.tasks.forEach((task, index) => {
        tasks.push(mapTaskCardToKanban(task, index));
      });
    });
    return tasks;
  }
}

export const kanbanService = new KanbanService();

export const mapCommentToUi = (comment: TaskComment): KanbanComment => ({
  id: String(comment.commentId),
  author: comment.author.name,
  authorId: String(comment.author.userId),
  text: comment.body,
  createdAt: comment.createdAt,
  attachments: comment.attachments ?? [],
});

export const buildBoardFiltersFromUi = (params: {
  search?: string;
  status?: string;
  assignee?: string;
  priority?: string;
  label?: string;
}): BoardFilters => {
  const filters: BoardFilters = {};
  if (params.search?.trim()) filters.search = params.search.trim();
  if (params.status && params.status !== 'all') {
    filters.status = [params.status as TaskStatus];
  }
  if (params.assignee && params.assignee !== 'all' && params.assignee !== 'unassigned') {
    filters.assigneeUserIds = [Number(params.assignee)];
  }
  if (params.priority && params.priority !== 'all') {
    filters.priorities = [params.priority as TaskPriority];
  }
  if (params.label && params.label !== 'all') {
    filters.labelIds = [Number(params.label)];
  }
  return filters;
};
